// Panel lateral con lista de canciones, buscador y filtro por categoría
import React, { useState, useMemo, useRef, useEffect } from "react";
import { canciones, categorias } from "../data/canciones";

function normalizar(texto) {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Resalta palabras buscadas en un texto, devuelve array de spans
function resaltarTexto(texto, palabras) {
  if (!palabras.length || !texto) return texto;
  const regex = new RegExp(`(${palabras.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const partes = texto.split(regex);
  return partes.map((parte, i) =>
    regex.test(parte)
      ? <em key={i} className="match-hl">{parte}</em>
      : parte
  );
}

// Extrae extractos de líneas donde aparecen las palabras buscadas (máx maxLineas)
function extraerExtractos(lineas, palabras, maxLineas = 5) {
  if (!lineas || !palabras.length) return [];
  const resultados = [];
  for (const linea of lineas) {
    if (!linea.texto) continue;
    const norm = normalizar(linea.texto);
    if (palabras.every(p => norm.includes(p))) {
      resultados.push(linea.texto);
      if (resultados.length >= maxLineas) break;
    }
  }
  return resultados;
}

// Cuenta total de líneas con coincidencias
function contarCoincidencias(lineas, palabras) {
  if (!lineas || !palabras.length) return 0;
  return lineas.filter(l => l.texto && palabras.every(p => normalizar(l.texto).includes(p))).length;
}

const CATS_DESTACADAS = ['Adoración', 'Comunión', 'Alabanza', 'Espíritu Santo', 'Entrada', 'Efusión', 'María', 'Sacerdotes'];

export function ListaCanciones({
  cancionEmitida,
  cancionLocal,
  onSeleccionar,
  onCategoriaExterna,
  categoriaExterna,
  terminoBusqueda,
  palabrasBusqueda,
}) {
  const [busqueda, setBusqueda] = useState("");
  const [ambito, setAmbito] = useState("indice"); // "indice" | "letra" | "todo"
  const [categoriaFiltro, setCategoriaFiltroLocal] = useState("Todas");
  const [orden, setOrden] = useState("numerico");
  const [catExpandida, setCatExpandida] = useState(false);
  const inputRef = useRef(null);

  const categoriaActiva = categoriaExterna ?? categoriaFiltro;
  const setCategoriaFiltro = (cat) => {
    setCategoriaFiltroLocal(cat);
    if (onCategoriaExterna) onCategoriaExterna(null);
  };

  useEffect(() => {
    if (categoriaExterna) setCategoriaFiltroLocal(categoriaExterna);
  }, [categoriaExterna]);

  const palabras = useMemo(() =>
    busqueda.trim().split(/\s+/).filter(Boolean).map(normalizar),
    [busqueda]
  );

  const cancionesFiltradas = useMemo(() => {
    let resultado = canciones.filter((c) => {
      // Filtro categoría
      if (categoriaActiva === "Sin categoría") {
        if (c.categorias && c.categorias.length > 0) return false;
      } else if (categoriaActiva !== "Todas") {
        if (!c.categorias || !c.categorias.includes(categoriaActiva)) return false;
      }

      // Filtro búsqueda
      if (!palabras.length) return true;

      if (ambito === "indice" || ambito === "todo") {
        const enTitulo = palabras.every(p => normalizar(c.titulo).includes(p));
        const enAutor = palabras.every(p => normalizar(c.autor || "").includes(p));
        const enNumero = palabras.every(p => (c.numero || "").includes(p));
        if (enTitulo || enAutor || enNumero) return true;
      }

      if (ambito === "letra" || ambito === "todo") {
        const todasLineas = [
          ...(c.lineas || []),
          ...((c.variantes || []).flatMap(v => v.lineas || []))
        ];
        if (todasLineas.some(l => l.texto && palabras.every(p => normalizar(l.texto).includes(p)))) return true;
      }

      return false;
    });

    if (orden === "alfabetico") {
      resultado = [...resultado].sort((a, b) =>
        normalizar(a.titulo).localeCompare(normalizar(b.titulo))
      );
    }
    return resultado;
  }, [palabras, ambito, categoriaActiva, orden]);

  const catsMostrar = catExpandida ? categorias : CATS_DESTACADAS.filter(c => categorias.includes(c));

  return (
    <aside className="panel-lista">
      {/* ── CABECERA ── */}
      <div className="lista-header">
        <h1 className="app-titulo">🎶 Cancionero</h1>
        <p className="app-subtitulo">{canciones.length} canciones</p>
      </div>

      {/* ── ZONA 1: BÚSQUEDA ── */}
      <div className="zona-busqueda">
        <div className="zona-titulo"><i className="zona-icon">🔍</i>Búsqueda</div>

        <div className="buscador-container">
          <div className="buscador-top">
            <span className="buscador-icono">🔍</span>
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="buscador-input"
            />
            {busqueda && (
              <button className="buscador-limpiar" onClick={() => setBusqueda("")}>✕</button>
            )}
          </div>
          <div className="ambito-row">
            {[["indice", "Índice"], ["letra", "Letra"], ["todo", "Todo"]].map(([val, label]) => (
              <button
                key={val}
                className={`ambito-btn ${ambito === val ? "activo" : ""}`}
                onClick={() => setAmbito(val)}
              >{label}</button>
            ))}
          </div>
        </div>

        <div className="orden-container">
          <button className={`orden-btn ${orden === "numerico" ? "activo" : ""}`} onClick={() => setOrden("numerico")}># Numérico</button>
          <button className={`orden-btn ${orden === "alfabetico" ? "activo" : ""}`} onClick={() => setOrden("alfabetico")}>A–Z Título</button>
        </div>
      </div>

      {/* ── ZONA 2: CATEGORÍA ── */}
      <div className="zona-categoria">
        <div className="zona-cat-header">
          <span className="zona-titulo"><i className="zona-icon">🏷</i>Categoría</span>
          <button className="cat-ver-todas" onClick={() => setCatExpandida(!catExpandida)}>
            {catExpandida ? "Ver menos ▲" : `Ver todas (${categorias.length}) ▼`}
          </button>
        </div>
        <div className="categorias-grid">
          <button
            onClick={() => setCategoriaFiltro("Todas")}
            className={`categoria-btn ${categoriaActiva === "Todas" ? "activa" : ""}`}
          >Todas</button>
          <button
            onClick={() => setCategoriaFiltro("Sin categoría")}
            className={`categoria-btn sin-cat ${categoriaActiva === "Sin categoría" ? "activa" : ""}`}
          >Sin categoría</button>
          {catsMostrar.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoriaFiltro(cat)}
              className={`categoria-btn ${categoriaActiva === cat ? "activa" : ""}`}
            >{cat}</button>
          ))}
        </div>
      </div>

      {/* ── ZONA 3: RESULTADOS ── */}
      <div className="zona-resultados">
        <div className="resultados-header">
          <span className="res-count">{cancionesFiltradas.length} resultado{cancionesFiltradas.length !== 1 ? "s" : ""}</span>
          {busqueda && <span className="busq-chip">"{busqueda}"</span>}
          {busqueda && <span className="modo-chip">en {ambito === "indice" ? "índice" : ambito === "letra" ? "letra" : "todo"}</span>}
          {categoriaActiva !== "Todas" && (
            <button className="filtro-activo-clear" onClick={() => setCategoriaFiltro("Todas")}>
              {categoriaActiva} ✕
            </button>
          )}
        </div>

        <div className="lista-resultados">
          <ul className="lista-canciones">
            {cancionesFiltradas.map((cancion) => {
              const esEmitida = cancionEmitida?.id === cancion.id;
              const esLocal = cancionLocal?.id === cancion.id;
              const sinCat = !cancion.categorias || cancion.categorias.length === 0;

              // Extractos de letra si aplica
              let extractos = [];
              let totalCoincidencias = 0;
              if (palabras.length && (ambito === "letra" || ambito === "todo")) {
                const todasLineas = [...(cancion.lineas || [])];
                extractos = extraerExtractos(todasLineas, palabras, 5);
                totalCoincidencias = contarCoincidencias(todasLineas, palabras);
              }

              return (
                <li
                  key={cancion.id}
                  onClick={() => onSeleccionar(cancion)}
                  className={`cancion-item ${esEmitida ? "emitida" : ""} ${esLocal ? "preview" : ""} ${sinCat ? "sin-cat-item" : ""}`}
                >
                  <span className="cancion-item-numero">{cancion.numero}</span>
                  <div className="cancion-item-texto">
                    <span className="cancion-item-titulo">
                      {palabras.length && (ambito === "indice" || ambito === "todo")
                        ? resaltarTexto(cancion.titulo, palabras)
                        : cancion.titulo}
                    </span>
                    {cancion.autor && (
                      <span className="cancion-item-autor">
                        {palabras.length && (ambito === "indice" || ambito === "todo")
                          ? resaltarTexto(cancion.autor, palabras)
                          : cancion.autor}
                      </span>
                    )}
                    {extractos.length > 0 && (
                      <div className="item-extractos">
                        {extractos.map((ext, i) => (
                          <span key={i} className="item-extracto">
                            «...{resaltarTexto(ext, palabras)}...»
                          </span>
                        ))}
                        {totalCoincidencias > 5 && (
                          <span className="item-extracto-mas">...y {totalCoincidencias - 5} líneas más</span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`cancion-item-cat ${sinCat ? "sin-cat-badge" : ""}`}>
                    {sinCat ? "Sin cat." : (cancion.categorias[0] || "")}
                  </span>
                  {esEmitida && <span className="cancion-item-indicador" title="Emitida a todos">📡</span>}
                  {esLocal && !esEmitida && <span className="cancion-item-indicador" title="Vista previa">👁</span>}
                </li>
              );
            })}
          </ul>
          {cancionesFiltradas.length === 0 && (
            <div className="lista-vacia">
              <p>No se encontraron canciones</p>
              <button onClick={() => { setBusqueda(""); setCategoriaFiltro("Todas"); }}>
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
